import useRectificationStore from '../stores/useRectificationStore';
import useElongationStore from '../stores/useElongationStore';
import useTrimlineStore from '../stores/useTrimlineStore';
import useFlareStore from '../stores/useFlareStore';
import useFlaresSmootheningStore from '../stores/useFlaresSmootheningStore';
import useMLReductionStore from '../stores/useMLReductionStore';
import useMLReductionSmootheningStore from '../stores/useMLReductionSmootheningStore';
import useCarveSmootheningStore from '../stores/useCarveSmootheningStore';
import useOutwardThicknessStore from '../stores/useOutwardThicknessStore';
import useValveStore from '../stores/useValveStore';
import useLockingSystemStore from '../stores/useLockingSystemStore';
import useLogoPlacementStore from '../stores/useLogoPlacementStore';
import useEditorStore from '../stores/useEditorStore';
import useAxesAlignmentStore from '../stores/useAxesAlignmentStore';
import { MAIN_PROCESS_STEPS, SCAN_PROCESS_STEPS } from '../constants/flowConfig';
import { isStepSkipOverridden } from './stepSkipOverrides';

/**
 * Steps that may NOT be skipped — the "Next" button stays disabled until at
 * least one of the step's tools has been applied.
 */
export const STEPS_REQUIRING_APPLY = new Set([
  MAIN_PROCESS_STEPS.TRIMLINE,
  MAIN_PROCESS_STEPS.THICKNESS,
]);

/**
 * Human-readable tool names listed in the "are you sure you want to skip?"
 * dialog, per step.
 */
const STEP_TOOL_NAMES = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: ['Trim', 'Lasso', 'Fill', 'Move', 'Rotate'],
  [MAIN_PROCESS_STEPS.RECTIFICATION]: [
    'Blocking Zone',
    'Global Reduction',
    'Measure Reduction',
    'Scoop Recharge',
    'Inflate / Deflate',
    'Smoothing',
    'Sculpt Smooth',
  ],
  [MAIN_PROCESS_STEPS.ELONGATION]: ['Elongation'],
  [MAIN_PROCESS_STEPS.TRIMLINE]: ['Trimline'],
  [MAIN_PROCESS_STEPS.FLARES]: ['Flare', 'Flares Smoothening'],
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: ['ML Reduction', 'Smoothing'],
  [MAIN_PROCESS_STEPS.THICKNESS]: ['Thickness'],
  [MAIN_PROCESS_STEPS.VALVE]: ['Valve'],
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: ['Locking System'],
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: ['Logo'],
};

/**
 * Per-step predicate: has the user applied at least one of the step's tools?
 * Reads live store state via getState() so it is safe to call imperatively
 * (e.g. inside the Next handler).
 */
const STEP_APPLIED_CHECKS = {
  // Preparing scan has no single "applied" flag; treat any edit pushed to the
  // editor history (trim, fill, lasso, move…) as having used a tool.
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: () =>
    useEditorStore.getState().historyIndex >= 0,
  [MAIN_PROCESS_STEPS.RECTIFICATION]: () => {
    const s = useRectificationStore.getState();
    return Boolean(
      s.isRectificationApplied ||
        s.isScoopRechargeApplied ||
        s.isGlobalReductionApplied ||
        s.isSmootheningApplied ||
        useCarveSmootheningStore.getState().isSmootheningApplied,
    );
  },
  [MAIN_PROCESS_STEPS.ELONGATION]: () =>
    Boolean(useElongationStore.getState().isElongationApplied),
  [MAIN_PROCESS_STEPS.TRIMLINE]: () => {
    const s = useTrimlineStore.getState();
    return Boolean(s.isTrimlineApplied || s.applied);
  },
  [MAIN_PROCESS_STEPS.FLARES]: () =>
    Boolean(useFlareStore.getState().isFlareApplied) ||
    Boolean(useFlaresSmootheningStore.getState().isSmootheningApplied),
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: () =>
    Boolean(useMLReductionStore.getState().mlReductionApplyConsumed) ||
    Boolean(useMLReductionSmootheningStore.getState().isSmootheningApplied),
  [MAIN_PROCESS_STEPS.THICKNESS]: () =>
    Boolean(useOutwardThicknessStore.getState().isApplied),
  [MAIN_PROCESS_STEPS.VALVE]: () => Boolean(useValveStore.getState().valveApplied),
  // Applied in-session (has an undoable command) OR restored as complete from a
  // saved checkpoint on resume — the attachment cut is baked into the mesh in
  // both cases, so neither should prompt to skip.
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: () => {
    const s = useLockingSystemStore.getState();
    return Boolean(
      s.currentAttachmentCommand ||
        s.bulldog?.phase === 'complete' ||
        s.fourHoleAttachment?.phase === 'complete',
    );
  },
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: () =>
    Boolean(useLogoPlacementStore.getState().logoApplied),
};

/** Tool display names for a step (empty array when the step has no tool gate). */
export function getStepToolNames(step) {
  return STEP_TOOL_NAMES[step] ?? [];
}

/**
 * Steps whose skip confirmation dialog is suppressed — pressing Next moves on
 * without prompting even when no tool was applied.
 */
export const STEPS_WITHOUT_SKIP_DIALOG = new Set([
  SCAN_PROCESS_STEPS.PREPARING_SCAN,
  MAIN_PROCESS_STEPS.ELONGATION,
]);

/** True when the step should prompt before skipping with nothing applied. */
export function stepShowsSkipDialog(step) {
  return !STEPS_WITHOUT_SKIP_DIALOG.has(step);
}

/**
 * Per-step overrides for the skip-confirmation prompt. Flares and ML Reduction
 * only warn when the main tool was applied but its smoothening was not — the
 * user is being reminded to smooth, not to use the tool. When the main tool was
 * never used there is nothing to smooth, so no dialog is shown.
 */
const STEP_SKIP_PROMPT_OVERRIDES = {
  // Rectification warns about the gating tools that have NOT been used yet —
  // Global Reduction, Scoop Recharge / Carve Relief, and Smoothing. Tools the
  // user already applied are dropped from the list, and once all three are used
  // there is nothing left to warn about so the dialog is skipped entirely.
  [MAIN_PROCESS_STEPS.RECTIFICATION]: () => {
    const s = useRectificationStore.getState();
    const toolNames = [
      ['Global Reduction', s.isGlobalReductionApplied],
      ['Scoop Recharge', s.isScoopRechargeApplied],
      ['Smoothing', useCarveSmootheningStore.getState().isSmootheningApplied],
    ]
      .filter(([, applied]) => !applied)
      .map(([name]) => name);
    return { show: toolNames.length > 0, toolNames };
  },
  [MAIN_PROCESS_STEPS.FLARES]: () => ({
    show:
      Boolean(useFlareStore.getState().isFlareApplied) &&
      !useFlaresSmootheningStore.getState().isSmootheningApplied,
    toolNames: ['Flares Smoothening'],
  }),
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: () => ({
    show:
      Boolean(useMLReductionStore.getState().mlReductionApplyConsumed) &&
      !useMLReductionSmootheningStore.getState().isSmootheningApplied,
    toolNames: ['Smoothing'],
  }),
};

/**
 * Whether to show the skip dialog for a step and, if so, which tool names to
 * list. Steps with a custom override use it; all others fall back to the
 * default rule (gated, skippable step with nothing applied).
 */
export function getSkipDialogPlan(step) {
  const override = STEP_SKIP_PROMPT_OVERRIDES[step];
  if (override) return override();
  const show =
    stepHasToolGate(step) &&
    !stepRequiresApply(step) &&
    stepShowsSkipDialog(step) &&
    !isStepToolApplied(step);
  return { show, toolNames: getStepToolNames(step) };
}

/** True when the step participates in the apply/skip flow. */
export function stepHasToolGate(step) {
  return Boolean(STEP_APPLIED_CHECKS[step]);
}

/**
 * True when at least one of the step's tools has been applied. Steps without a
 * tool gate are treated as already satisfied so they never block navigation.
 */
export function isStepToolApplied(step) {
  const check = STEP_APPLIED_CHECKS[step];
  return check ? check() : true;
}

/**
 * True when the step cannot be skipped (Next disabled until applied). The
 * `VITE_SKIPPABLE_STEPS` env allowlist downgrades a listed step to a normal
 * skippable one (skip confirmation still applies).
 */
export function stepRequiresApply(step) {
  return STEPS_REQUIRING_APPLY.has(step) && !isStepSkipOverridden(step);
}

/**
 * Non-gated steps still produce work the user could lose on a backward
 * navigation. Map them to their own "dirty" signal so the discard prompt only
 * appears when there is actually something to discard.
 */
const STEP_CHANGE_CHECKS = {
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: () => {
    const s = useAxesAlignmentStore.getState();
    return Boolean(s.isAligned) || Boolean(s.areAllSelectionsCompleted?.());
  },
};

/**
 * True when the user has made a discardable change on this step. Tool-gated
 * steps reuse their apply check; other steps fall back to a step-specific
 * signal. Steps with neither are treated as clean (nothing to discard).
 */
export function stepHasUserChanges(step) {
  if (stepHasToolGate(step)) return isStepToolApplied(step);
  const check = STEP_CHANGE_CHECKS[step];
  return check ? check() : false;
}
